import styled from "styled-components";

import { DESKTOP_BREAKPOINT } from "../../components/AppShell/styles";

const MOBILE = `not all and (min-width: ${DESKTOP_BREAKPOINT})`;

export const Container = styled.div`
  padding-bottom: 2rem;
`;

/* A titled block of the screen: small uppercase label + card (same as /settings) */
export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.5rem 2rem 0;

  @media ${MOBILE} {
    padding: 20px 20px 0;
  }
`;

/* Blocks the desktop sidebar already covers (e.g. Ajuda and Feedback) */
export const MobileOnlySection = styled(Section)`
  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    display: none;
  }
`;

export const Card = styled.div`
  padding: 20px;
  background: var(--color-neutral-dark);
  border-radius: 1rem;
`;

export const Profile = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;

  .avatar {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    font-family: Poppins, sans-serif;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--color-neutral-white);
    text-transform: uppercase;
    background: var(--color-brand-blue);
    border-radius: 100%;
  }

  .text {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
  }

  h6,
  p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  p {
    font-size: 0.875rem;
    color: var(--color-neutral-150);
  }
`;

/* Rows that open something: reads as a list of links, like /settings' advanced options */
export const List = styled.ul`
  overflow: hidden;
  background: var(--color-neutral-dark);
  border-radius: 1rem;

  li + li {
    border-top: 1px solid var(--color-neutral-base);
  }

  button {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    width: 100%;
    padding: 1rem 20px;
    text-align: left;
    transition: background-color 0.15s;

    &:hover {
      background: rgba(255, 255, 255, 0.03);
    }
  }

  svg {
    flex-shrink: 0;
  }

  .title {
    flex: 1;
    font-family: Inter, sans-serif;
    font-size: 0.875rem;
    font-weight: 700;
    line-height: 1.25rem;
    color: var(--color-neutral-white);
  }

  .value {
    font-family: Inter, sans-serif;
    font-size: 0.875rem;
    color: var(--color-neutral-150);
  }

  .chevron {
    display: flex;
    color: var(--color-neutral-150);
  }
`;

export const Footer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.5rem;
  padding: 1.5rem 2rem 0;

  @media ${MOBILE} {
    padding: 1.5rem 20px 0;
  }

  a,
  button {
    font-family: Inter, sans-serif;
    font-size: 0.75rem;
    color: var(--color-neutral-150);

    &:hover {
      text-decoration: underline;
    }
  }

  /* "Excluir conta": on the right, in red */
  .danger {
    margin-left: auto;
    color: var(--color-feedback-danger);
  }
`;

/* The language modal's choices, as the Domains "connect a Keepo" list: flag, name in itself and in the editor's
   language, "Current" on the one in use, and a radio */
export const LangOptions = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  button {
    display: flex;
    gap: 0.875rem;
    align-items: center;
    width: 100%;
    padding: 0.75rem 1rem 0.75rem 0.75rem;
    font-family: Inter, sans-serif;
    text-align: left;
    background: rgba(255, 255, 255, 0.02);
    border: 1px solid var(--color-neutral-base);
    border-radius: 0.875rem;
    transition:
      border-color 0.15s,
      background-color 0.15s;

    &:hover {
      border-color: var(--color-neutral-light);
    }

    &.selected {
      background: rgba(255, 255, 255, 0.04);
      border-color: var(--color-brand-plus);
      box-shadow: inset 0 0 0 1px var(--color-brand-plus);
    }

    > svg {
      flex-shrink: 0;
      border-radius: 10px;
      box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.12);
    }
  }

  .text {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;

    b {
      font-size: 0.9375rem;
      font-weight: 700;
      color: var(--color-neutral-white);
    }

    span {
      font-size: 0.75rem;
      color: var(--color-neutral-150);
    }
  }

  .current {
    flex-shrink: 0;
    padding: 0.125rem 0.5rem;
    font-size: 0.6875rem;
    font-weight: 700;
    color: var(--color-neutral-150);
    background: var(--color-neutral-base);
    border-radius: 100px;
  }

  .radio {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    border: 2px solid var(--color-neutral-light);
    border-radius: 100%;
  }

  .selected .radio {
    border: 5px solid var(--color-brand-plus);
  }
`;

/* Deleting the account: a card like the others below the preferences, only its button in the danger color */
export const DangerCard = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  background: var(--color-neutral-dark);
  border-radius: 1rem;

  @media ${MOBILE} {
    flex-direction: column;
    align-items: stretch;
  }

  .text {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  p {
    font-family: Inter, sans-serif;
    font-size: 0.8125rem;
    line-height: 1.125rem;
    color: var(--color-neutral-150);
  }

  button {
    flex-shrink: 0;
    padding: 0.5rem 1rem;
    font-family: Inter, sans-serif;
    font-size: 0.8125rem;
    font-weight: 700;
    color: var(--color-feedback-danger);
    border: 1px solid var(--color-neutral-base);
    border-radius: 100px;
    transition: border-color 0.15s;

    &:hover {
      border-color: var(--color-feedback-danger);
    }
  }
`;

/* Deleting the account with Keepo Plus on: the Keepos to cancel first */
export const PlusKeepos = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  li {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 0.75rem 0.75rem 1rem;
    background: var(--color-neutral-base);
    border-radius: 12px;

    .keepo {
      min-width: 0;
      overflow: hidden;
      font-family: Inter, sans-serif;
      font-size: 0.875rem;
      font-weight: 700;
      color: var(--color-neutral-white);
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    button {
      flex-shrink: 0;
      padding: 0.5rem 0.875rem;
      font-family: Inter, sans-serif;
      font-size: 0.8125rem;
      font-weight: 700;
      color: var(--color-neutral-white);
      background: var(--color-neutral-dark);
      border-radius: 100px;
    }
  }
`;
